import React, { useState, useEffect, useCallback } from 'react';
import { Sun, ArrowRight, ArrowLeft, CheckCircle, Save, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert, ProgressBar } from '../design-system';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// Task-specific configuration for Gratitude Journaling
export const TaskGratitudeJournalingConfig = {
  'task_016': {
    sequence: 1,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'One good thing that happened to me today…',
        type: 'text',
        required: true,
        placeholder: 'What good thing happened to you today?'
      },
      {
        id: 'q2',
        text: 'Something good that I saw someone do…',
        type: 'text',
        required: true,
        placeholder: 'Describe something good you saw someone do...'
      },
      {
        id: 'q3',
        text: 'Today I had fun when…',
        type: 'text',
        required: true,
        placeholder: 'What did you have fun doing today?'
      }
    ]
  },
  'task_017': {
    sequence: 2,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'Something I accomplished today…',
        type: 'text',
        required: true,
        placeholder: 'What did you accomplish today?'
      },
      {
        id: 'q2',
        text: 'Something funny that happened today…',
        type: 'text',
        required: true,
        placeholder: 'What made you laugh or smile today?'
      },
      {
        id: 'q3',
        text: 'Someone I was thankful for today…',
        type: 'text',
        required: true,
        placeholder: 'Who are you thankful for today and why?'
      }
    ]
  },
  'task_018': {
    sequence: 3,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'Something I was thankful for today…',
        type: 'text',
        required: true,
        placeholder: 'What are you thankful for today?'
      },
      {
        id: 'q2',
        text: 'Today I smiled when…',
        type: 'text',
        required: true,
        placeholder: 'What made you smile today?'
      },
      {
        id: 'q3',
        text: 'Something about today I\'ll always want to remember…',
        type: 'text',
        required: true,
        placeholder: 'What special moment from today do you want to remember?'
      }
    ]
  },
  'task_019': {
    sequence: 4,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'One good thing that happened to me today…',
        type: 'text',
        required: true,
        placeholder: 'What good thing happened to you today?'
      },
      {
        id: 'q2',
        text: 'Today was special because…',
        type: 'text',
        required: true,
        placeholder: 'What made today special?'
      },
      {
        id: 'q3',
        text: 'Today I was proud of myself because…',
        type: 'text',
        required: true,
        placeholder: 'What are you proud of yourself for today?'
      }
    ]
  },
  'task_020': {
    sequence: 5,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'Something interesting that happened today…',
        type: 'text',
        required: true,
        placeholder: 'What interesting thing happened today?'
      },
      {
        id: 'q2',
        text: 'Someone I was thankful for today…',
        type: 'text',
        required: true,
        placeholder: 'Who are you thankful for today and why?'
      },
      {
        id: 'q3',
        text: 'Today I had fun when…',
        type: 'text',
        required: true,
        placeholder: 'What did you have fun doing today?'
      }
    ]
  },
  'task_021': {
    sequence: 6,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'Something about today I\'ll always want to remember…',
        type: 'text',
        required: true,
        placeholder: 'What special moment from today do you want to remember?'
      },
      {
        id: 'q2',
        text: 'Something funny that happened today…',
        type: 'text',
        required: true,
        placeholder: 'What made you laugh or smile today?'
      },
      {
        id: 'q3',
        text: 'My favorite part of today…',
        type: 'text',
        required: true,
        placeholder: 'What was your favorite part of today?'
      }
    ]
  },
  'task_022': {
    sequence: 7,
    prerequisites: [],
    questions: [
      {
        id: 'q1',
        text: 'Something I was happy about today…',
        type: 'text',
        required: true,
        placeholder: 'What made you happy today?'
      },
      {
        id: 'q2',
        text: 'Something good I saw someone do today…',
        type: 'text',
        required: true,
        placeholder: 'Describe something good you saw someone do...'
      },
      {
        id: 'q3',
        text: 'Something I did well today…',
        type: 'text',
        required: true,
        placeholder: 'What did you do well today?'
      }
    ]
  }
};

const TaskGratitudeJournaling = ({ task, taskType, session, onComplete }) => {
  const { user, userInfo } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);

  // Get questions from task-specific configuration
  const questions = TaskGratitudeJournalingConfig[task.taskTypeId]?.questions || [];

  // Auto-save functionality
  const saveProgress = useCallback(async (questionId, answer, questionIndex) => {
    if (!session?.sessionId) return;

    setIsSaving(true);
    try {
      await apiPost(
        '/.netlify/functions/journalingTaskApi',
        {
          sessionId: session.sessionId,
          questionId: questionId,
          answer: answer,
          currentQuestionIndex: questionIndex
        },
        user,
        userInfo?.userId
      );

      setLastSaved(new Date());
      
      // Progress saved successfully - no auto-completion logic
      // Don't show toast for auto-save to avoid spam
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, task?.taskId, user, userInfo?.userId]);

  // Debounced auto-save
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (questionId, answer, questionIndex) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(questionId, answer, questionIndex);
        }, 10000); // 10 seconds
      };
    })(),
    [saveProgress]
  );

  // Load progress from session (following onboarding pattern)
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing answers if session has data
    if (session.sessionData?.answers) {
      setAnswers(session.sessionData.answers);
      setCurrentQuestionIndex(session.sessionData.currentQuestionIndex || 0);
    }
  }, [session, hasLoadedProgress]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));

    // Auto-save with debouncing
    debouncedSave(questionId, value, currentQuestionIndex);
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      // Save current answer immediately before moving to next question
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && answers[currentQuestion.id]) {
        try {
          await saveProgress(currentQuestion.id, answers[currentQuestion.id], currentQuestionIndex);
        } catch (error) {
          console.error('Error saving progress on next:', error);
          // Don't block navigation if save fails
        }
      }
      
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = async () => {
    if (currentQuestionIndex > 0) {
      // Save current answer immediately before moving to previous question
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion && answers[currentQuestion.id]) {
        try {
          await saveProgress(currentQuestion.id, answers[currentQuestion.id], currentQuestionIndex);
        } catch (error) {
          console.error('Error saving progress on previous:', error);
          // Don't block navigation if save fails
        }
      }
      
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleManualSave = async () => {
    if (!currentQuestion) return;

    const currentAnswer = answers[currentQuestion.id] || '';
    await saveProgress(currentQuestion.id, currentAnswer, currentQuestionIndex);
    toast.success('Progress saved!');
  };

  const handleComplete = async () => {
    if (!currentQuestion) return;

    setIsSubmitting(true);
    try {
      // First, save completion data to the session
      const completionData = {
        answers: answers,
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/journalingTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        user,
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        user,
        userInfo?.userId
      );

      toast.success('Gratitude journaling completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const isFirstQuestion = currentQuestionIndex === 0;

  if (!currentQuestion) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Sun className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading journaling questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
            <Sun className="h-6 w-6 text-yellow-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{taskType.pageTitle}</h1>
            <p className="text-gray-600">{taskType.description}</p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <ProgressBar
          current={currentQuestionIndex + 1}
          total={questions.length}
          variant="yellow"
          size="medium"
          showPercentage={true}
          label={`Question ${currentQuestionIndex + 1} of ${questions.length}`}
        />
      </div>

      {/* Current Question */}
      <Card variant="elevated" padding="large" className="mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {currentQuestion.text}
              {currentQuestion.required && <span className="text-yellow-500 ml-1">*</span>}
            </label>
            <Input
              type="textarea"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
              placeholder={currentQuestion.placeholder}
              rows={6}
              className="resize-none"
            />
          </div>
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          variant="ghost"
          size="medium"
          className="flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        <div className="flex items-center space-x-4">
          <Button
            onClick={handleManualSave}
            disabled={isSaving}
            variant="ghost"
            size="medium"
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          {isLastQuestion ? (
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
          variant="accent"
          size="medium"
          className="flex items-center"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          {isSubmitting ? 'Completing...' : 'Complete Journaling'}
        </Button>
      ) : (
        <Button
          onClick={handleNext}
          variant="accent"
              size="medium"
              className="flex items-center"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </div>

      {/* Save Status */}
      {lastSaved && (
        <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
          <Clock className="h-4 w-4 mr-2" />
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}

      {/* Helpful Tips */}
      <Alert variant="info" className="mt-8">
        <h3 className="text-sm font-medium text-yellow-800 mb-2">💡 Journaling Tips</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• There are no right or wrong answers - write from your heart</li>
          <li>• Take your time to reflect on the positive moments of your day</li>
          <li>• Your progress is automatically saved every 10 seconds</li>
          <li>• You can navigate between questions freely</li>
          <li>• Focusing on gratitude can help improve your mood and well-being</li>
        </ul>
      </Alert>
    </div>
  );
};

// Add static methods to the component for Summary and ResultsSummary
TaskGratitudeJournaling.Summary = ({ task, session }) => {
  const answeredCount = session?.answers ? Object.keys(session.answers).length : 0;
  const totalQuestions = TaskGratitudeJournalingConfig[task.taskTypeId]?.questions?.length || 0;
  
  return (
    <div className="gratitude-journaling-summary">
      <div className="flex items-center space-x-2">
        <Sun className="h-4 w-4 text-yellow-600" />
        <span className="text-xs text-gray-500">
          {answeredCount}/{totalQuestions} questions answered
        </span>
      </div>
    </div>
  );
};

TaskGratitudeJournaling.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData?.answers) return null;

  const questions = TaskGratitudeJournalingConfig[task.taskTypeId]?.questions || [];
  
  return (
    <div className="gratitude-journaling-results-summary">
      <h3 className="font-medium text-gray-900 mb-2">Gratitude Journaling Results - {patientName}</h3>
      <div className="space-y-3 text-sm">
        {questions.map(question => (
          <div key={question.id} className="border-l-2 border-yellow-200 pl-3">
            <p className="font-medium text-gray-700 mb-1">{question.text}</p>
            <p className="text-gray-600">{completionData.answers[question.id] || 'No answer provided'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Add static method for task assignment form
TaskGratitudeJournaling.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  // No custom form needed - return null
  return null;
};

export default TaskGratitudeJournaling;

